/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState } from "react";
import { Unlink, X, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const COUNT_LABEL = {
  subcontracts: ["分包合同", "Subcontracts"], tasksDone: ["已完成任务", "Tasks done"], files: ["项目文件", "Files"], changeOrders: ["变更单", "Change orders"],
  notes: ["项目留言", "Notes"], collected: ["已收款", "Collected"], started: ["已开工 / 阶段推进", "Started / phase > 0"],
};

/* 误关联清理：阶段未签约却关联了项目档案 → 管理层解除关联；空档案可同时回收到回收站（可恢复）。 */
export function UnlinkProjectDialog({ open, leadId, zh, api, onClose, onDone }) {
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState("");
  const [recycle, setRecycle] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setInfo(null); setReason(""); setBusy(false);
    api.get(`/crm/leads/${leadId}/project-link`).then(({ data }) => { setInfo(data); setRecycle(!!data.canRecycle); }).catch((e) => toast.error(e?.detailText || e.message));
  }, [open, leadId]); // eslint-disable-line
  if (!open) return null;
  const nonEmpty = Object.entries(info?.counts || {}).filter(([, v]) => v > 0);
  const submit = async () => {
    if (reason.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/unlink-project`, { reason: reason.trim(), recycle: recycle && info?.canRecycle });
      toast.success(zh ? (data.recycled ? `已解除关联，空档案 ${data.projectId} 已移入回收站` : `已解除与 ${data.projectId} 的关联（档案保留）`) : (data.recycled ? "Unlinked · empty project recycled" : "Unlinked"));
      onDone?.(data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "操作失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="unlink-project-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <div className="flex-wrap md:flex-nowrap flex items-center justify-between px-5 py-3.5 bg-amber-50 border-b border-amber-100">
          <p className="text-[15px] font-bold text-amber-800 flex items-center gap-2"><Unlink className="h-4 w-4" />{zh ? "解除误关联的项目档案" : "Unlink mis-linked project"}</p>
          <button onClick={onClose} data-testid="unlink-project-close" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!info ? <p className="text-xs text-slate-400">{zh ? "检查档案内容…" : "Checking…"}</p> : (
            <>
              <p className="text-[12.5px] text-slate-600" data-testid="unlink-project-info">
                {zh ? "项目档案 " : "Project "}<b>{info.projectId}</b>{info.project?.address ? ` · ${info.project.address}` : ""}
                {info.project?.createdFrom && <span className="text-slate-400"> · {zh ? "来源" : "from"} {info.project.createdFrom}</span>}
              </p>
              {info.canRecycle ? (
                <label className="flex items-start gap-2 text-[12.5px] text-slate-700 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 cursor-pointer" data-testid="unlink-project-recycle-wrap">
                  <input type="checkbox" data-testid="unlink-project-recycle" checked={recycle} onChange={(e) => setRecycle(e.target.checked)} className="mt-0.5" />
                  <span><Trash2 className="inline h-3.5 w-3.5 mr-1 text-emerald-700" />{zh ? "该档案为空档案（无分包 / 任务 / 文件 / 变更单 / 收款 / 开工记录），同时回收到回收站（可在回收站恢复）" : "Empty project — also move it to the recycle bin (restorable)"}</span>
                </label>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="unlink-project-nonempty">
                  <p className="font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{zh ? "档案非空，只能解除关联，不会回收" : "Project has content — unlink only"}</p>
                  <p className="mt-0.5">{nonEmpty.map(([k, v]) => `${zh ? COUNT_LABEL[k]?.[0] : COUNT_LABEL[k]?.[1]} ${v}`).join(" · ")}</p>
                </div>
              )}
              <textarea data-testid="unlink-project-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300}
                placeholder={zh ? "解除原因（必填）：如 新线索误点补建 / 关联到错误项目…" : "Reason (required)"}
                className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-200" />
              <p className="text-[11px] text-slate-400">{zh ? "将写入系统跟进记录与审计；客户阶段不变。" : "Logged to follow-ups and audit; lead stage unchanged."}</p>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pt-1">
                <button onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-9 px-4 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-100">{zh ? "取消" : "Cancel"}</button>
                <button onClick={submit} disabled={busy || reason.trim().length < 2 || !info.canUnlink} data-testid="unlink-project-confirm"
                  className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-9 px-4 rounded-lg text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1">
                  <Unlink className="h-4 w-4" />{busy ? "…" : (zh ? (recycle && info.canRecycle ? "解除并回收空档案" : "仅解除关联") : (recycle && info.canRecycle ? "Unlink & recycle" : "Unlink only"))}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
